import { Box, Typography } from "@mui/material";
import * as React from "react"
import { useDispatch, useSelector } from "react-redux";
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Fluid from 'components/Fluid'
import { HeadContext } from 'components/Head/HeadContext';
import { setOpaque, setSmoke, setTransparent } from 'components/Navigation/slice';
import { ReactComponent as ArrowLeft } from 'images/icons/arrow_left_long.svg';
import { ReactComponent as ArrowRight } from 'images/icons/arrow_right_long.svg';
import loadImage from 'utils/loadImage';
import theme from 'utils/theme';
import zparse from 'utils/zparse';

import { setSize } from './slice';

import 'swiper/css';
import 'swiper/css/effect-fade';

const Masthead = props => {
	/**
	 * The mobileImage & mobileVideo props were left over from porting this from the We Are Z site.
	 * Mobile Video cannot be set in the CMS, but the support is there in the component if we need to add the support.
	 * The mobileImage support was never built into this component, and so the prop is commented out to avoid a compiler issue.
	 *  
	 */

	const { size, title, subtitle, video, /*mobileImage,*/ mobileVideo, body, gallery, titleClassName } = props;
	let image = props.image;
	const cdnDomain = process.env.CDN_DOMAIN ?? '';
	const [backgroundLoaded, setBackgroundLoaded] = React.useState(null);
	const dispatch = useDispatch();
	const mastheadRef = React.useRef();
	const variant = useSelector((state) => state.nav.variant);
	const navOpen = useSelector((state) => state.nav.open);
	const mastheadSize = useSelector((state) => state.masthead.size);
	const { addHead } = React.useContext(HeadContext);

	const inlineStyle = {};

	const [galleryKey, setGalleryKey] = React.useState(0);
	const swiperRef = React.useRef(null);

	if (cdnDomain && image && image.indexOf(cdnDomain) !== 0 && !/^https?:\/\//.test(image)) {
		image = `${cdnDomain}${image}`;
	}

	if (image && ((size !== 'no-image') || (gallery?.slides?.length))) {
		inlineStyle.backgroundImage = `url('${image}?max-w=1920&format=webp')`;
	}
	if ((size !== 'no-image') || gallery?.slides?.length) {
		inlineStyle.backgroundImage = `linear-gradient(180deg, rgba(0, 0, 0, 0.45) 0%, rgba(0, 0, 0, 0.00) 54.81%), ${inlineStyle.backgroundImage}`;
	}

	React.useEffect(() => {
		if (!!mastheadRef.current === false) return;
		if (image && ((size !== 'no-image') || (gallery?.slides?.length))) {
			loadImage(`${image}?max-w=1920&format=webp`)
				.then((img) => {
					setBackgroundLoaded(img.src);
					mastheadRef.current.classList.add('background-loaded');
				})
				.catch(() => {
					setBackgroundLoaded(false);
				});
		}
	}, [image, size, gallery?.slides?.length, mastheadRef]);

	const mobileMedia = theme.breakpoints.between('xs', 'sm').replace('@media ', '');
	const desktopMedia = theme.breakpoints.up('sm').replace('@media ', '');

	React.useEffect(() => {
		if (gallery?.slides?.length) {
			dispatch(setSize('medium'));
		} else {
			dispatch(setSize(size));
		}
	}, [dispatch, size, gallery]);

	React.useEffect(() => {
		if (swiperRef.current) {
			setGalleryKey(s => s + 1);
		}
	}, [swiperRef]);

	React.useEffect(() => {
		if (!!mastheadRef.current === false) return;
		if (navOpen) return; // This prevents the intersection observer / scroll position from setting the open menu to a transparent or smoked variant.
		if (mastheadSize === 'no-image' || backgroundLoaded === false) {
			dispatch(setOpaque());
			return;
		}
		if (backgroundLoaded === null) return;
		const observer = new IntersectionObserver(([entry]) => {
			if (!entry.isIntersecting) dispatch(setSmoke());
			else {
				dispatch(setTransparent());
			}
		}, {
			root: null,
			threshold: .5,
			rootMargin: '0px',
		});
		observer.observe(mastheadRef.current);
		return ((element, observer) => ( // It's a complicated looking statement, but it's really just returning a closure with the element and observer as arguments so that it can be unobserved before React kills the ref.
			() => {
				observer.unobserve(element);
			}
		))(mastheadRef.current, observer);
	}, [variant, dispatch, mastheadRef, mastheadSize, navOpen, backgroundLoaded]);

	const showGalleryRow = Boolean(gallery?.slides?.length) || size === 'medium';

	if (video && image) {
		addHead(`<link rel="preload" as="image" href="${image}?max-h=720&format=webp" fetchpriority="high">`);
	}

	return (
		<Box ref={mastheadRef} className={`masthead masthead--${size}${video ? ' masthead--withVideo' : ''}${(gallery && gallery?.slides?.length) ? ' masthead--withGallery' : ''}`} style={((Object.keys(inlineStyle).length > 0) && (size === 'full-height') ? inlineStyle : undefined)}>
			<div className="masthead__overlay">
				{((size === 'small' || size === 'medium' || gallery?.slides?.length) && !video) && image && <Box className={'masthead--image'} style={((Object.keys(inlineStyle).length > 0) ? inlineStyle : undefined)} />}
				{!showGalleryRow && <div className={`masthead__content${!body ? ' masthead__content--no-body' : ''}`}>
					{subtitle && <Typography component="div" className="masthead__content__subtitle" variant={(size === 'no-image') ? "h_small" : "h3"}>{zparse(subtitle)}</Typography>}
					{title && (
						<Fluid>
							<Typography component="h1" className={`masthead__content__title ${titleClassName ?? ''}`} variant={(size === 'full-height') ? "display" : 'h1'}>{zparse(title)}</Typography>
						</Fluid>
					)}
					{body && size !== 'full-height' && <Typography component="div" className="masthead__content__body" variant="body">{zparse(body)}</Typography>}
				</div>}
			</div>
			{video &&
				<video className="masthead__video" playsInline autoPlay muted loop poster={image && `${image}?max-h=720&format=webp`} fetchpriority="high">
					{mobileVideo && <source media={mobileMedia} src={`${process.env.CDN_DOMAIN || ''}${mobileVideo}`} type="video/mp4" />}
					<source media={desktopMedia} src={`${process.env.CDN_DOMAIN || ''}${video}`} type="video/mp4" fetchpriority="high" />
				</video>
			}
			{showGalleryRow &&
				<Fluid className={`masthead__gallery_row ${title?.length > 20 ? 'masthead__gallery_row--long-title' : ''}`} layout={{ xs: '12', sm: `${gallery?.slides?.length ? '5 6' : '8'}` }} justifyContent={{ xs: 'start', md: 'space-between' }}>
					<div className="masthead__gallery_content">
						{subtitle && <Typography component="div" className="masthead__gallery_content__subtitle" variant="h3">{subtitle}</Typography>}
						{title && <Typography component="h1" className="masthead__gallery_content__title" variant="display">{title}</Typography>}
						{body && <Typography component="div" className="masthead__content__body" variant="p">{body}</Typography>}
					</div>
					<div className="masthead__gallery_container">
						{gallery?.slides?.length && <Swiper
							modules={[Pagination, Autoplay, EffectFade]}
							slidesPerView={1}
							slidesPerGroup={1}
							effect={'fade'}
							fadeEffect={{
								crossFade: true
							}}
							autoplay={{
								// delay: 1000,
								pauseOnMouseEnter: true,
							}}
							// autoplay={false}
							loop={true}
							className={"masthead__gallery"}
							speed={1000}
							pagination={{
								clickable: true,
								type: 'bullets',
								bulletClass: 'masthead__gallery_bullet',
								bulletActiveClass: 'masthead__gallery_bullet-active',
								bulletElement: 'button',
								modifierClass: 'masthead__gallery_',
								enabled: true
							}}
							key={`${galleryKey}`}
							onBeforeInit={(swiper) => {
								swiperRef.current = swiper;
							}}
						>
							{gallery?.slides.map((slide, index) => {
								return (
									<SwiperSlide key={index} className="masthead__gallery_slide">
										<Box
											className="masthead__gallery_slide_background"
											sx={{
												backgroundImage: `url(${slide?.image}?format=webp&max-w=1024)`,
											}} />
									</SwiperSlide>
								)
							})}
							{gallery?.slides?.length > 1 && <div className="masthead__gallery_navigation">
								<button onClick={() => swiperRef.current?.slidePrev()}><ArrowLeft /></button>
								<button onClick={() => swiperRef.current?.slideNext()}><ArrowRight /></button>
							</div>}
						</Swiper>}
					</div>
				</Fluid>
			}
		</Box>
	)
}

export default React.memo(Masthead);
